/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { Button, Modal } from "@duxton-web/ui"

import { useEcomMigrationDeadlineClock } from "ecommerce/hooks/useEcomMigrationDeadlineClock"
import { parseEcomMigrationTimestamp } from "ecommerce/utils/ecomMigration"
import { formatDate } from "utils/date_formatter"

import styles from "./CatalogUpgradeModal.modules.css"

interface CatalogUpgradeModalProps {
  open: boolean
  reviewEndAt: string
  /** Skip/dismiss: stay on the current page. */
  onSkip: () => void
  /** Review suggestions: navigate to the migration review route. */
  onReview: () => void
}

// TODO(BP-63345): Move this temporary copy to PhraseApp after the approved
// catalog-upgrade keys are available in the remote Phrase project.
const untranslatedTextMap = {
  titlePrefix: "Your catalog will upgrade on",
  description:
    "Review your suggested product groupings now. If you take no action, updates will apply automatically on that date.",
  productName: "Baseball Cap",
  variesBy: "Varies by",
  color: "Color",
  blue: "Blue",
  bluePrice: "$15.00",
  red: "Red",
  redPrice: "$14.00",
  yellow: "Yellow",
  yellowPrice: "$12.00",
  skip: "Skip",
  reviewSuggestions: "Review suggestions",
}

const productImages = {
  cap: require("assets/images/ecommerce/catalog-upgrade-modal/baseball-caps.png"),
  blue: require("assets/images/ecommerce/catalog-upgrade-modal/baseball-cap-blue.png"),
  red: require("assets/images/ecommerce/catalog-upgrade-modal/baseball-cap-red.png"),
  yellow: require("assets/images/ecommerce/catalog-upgrade-modal/baseball-cap-yellow.png"),
}

export const CatalogUpgradeModal: FC<CatalogUpgradeModalProps> = ({
  open,
  reviewEndAt,
  onSkip,
  onReview,
}) => {
  const textMap = untranslatedTextMap
  const reviewEndAtMs = parseEcomMigrationTimestamp(reviewEndAt)
  const { nowMs } = useEcomMigrationDeadlineClock({
    deadlineMs: reviewEndAtMs,
  })

  if (reviewEndAtMs === null || reviewEndAtMs <= nowMs) {
    return null
  }

  const reviewEndAtText = formatDate(new Date(reviewEndAtMs), "DD MMMM YYYY")

  return (
    <Modal
      open={open}
      onCancel={onSkip}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      width={624}
      className={styles.modal}
    >
      <div className={styles.content}>
        <div className={styles.preview}>
          <div className={styles.productCard}>
            <div className={styles.productSummary}>
              <img
                className={styles.productImage}
                src={productImages.cap}
                alt=""
              />
              <div className={styles.productDetails}>
                <div className={styles.productName}>{textMap.productName}</div>
                <div className={styles.productAttribute}>
                  <span className={styles.variesBy}>{textMap.variesBy}</span>
                  <span className={styles.attributeTag}>{textMap.color}</span>
                </div>
              </div>
            </div>
            <div className={styles.variationList}>
              <div className={styles.variationRow}>
                <img
                  className={styles.variationImage}
                  src={productImages.blue}
                  alt=""
                />
                <div className={styles.variationDetails}>
                  <span className={styles.variationName}>{textMap.blue}</span>
                  <span className={styles.variationPrice}>
                    {textMap.bluePrice}
                  </span>
                </div>
              </div>
              <div className={styles.variationRow}>
                <img
                  className={styles.variationImage}
                  src={productImages.red}
                  alt=""
                />
                <div className={styles.variationDetails}>
                  <span className={styles.variationName}>{textMap.red}</span>
                  <span className={styles.variationPrice}>
                    {textMap.redPrice}
                  </span>
                </div>
              </div>
              <div className={styles.variationRow}>
                <img
                  className={styles.variationImage}
                  src={productImages.yellow}
                  alt=""
                />
                <div className={styles.variationDetails}>
                  <span className={styles.variationName}>{textMap.yellow}</span>
                  <span className={styles.variationPrice}>
                    {textMap.yellowPrice}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.body}>
          <div className={styles.copy}>
            <h2 className={styles.title}>
              {textMap.titlePrefix} {reviewEndAtText}
            </h2>
            <p className={styles.description}>{textMap.description}</p>
          </div>
          <div className={styles.actions}>
            <Button className={styles.skipButton} onClick={onSkip}>
              {textMap.skip}
            </Button>
            <Button
              type="primary"
              className={styles.reviewButton}
              onClick={onReview}
            >
              {textMap.reviewSuggestions}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
